import React from 'react';
import { motion } from 'framer-motion';
import { CTAButton } from './CTAButton';
export function Mechanism() {
  return <section className="bg-[#FAFAF8] py-20 md:py-28" data-id="element-131">
      <div className="max-w-6xl mx-auto px-6" data-id="element-132">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16" data-id="element-133">
          {/* Main Copy — spans 2 columns */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.6
        }} className="lg:col-span-2" data-id="element-134">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1A1A1A] tracking-tight mb-10 leading-tight" data-id="element-135">
              Your Follicles Aren't Dead. And the Problem Isn't Only DHT.
            </h2>

            <div className="space-y-6 text-lg text-[#4B5563] leading-relaxed" data-id="element-136">
              <p data-id="element-137">
                The narrative you've been sold — that hair loss is genetic,
                permanent, and inevitable unless you block DHT — is incomplete.
                Not wrong. Incomplete.
              </p>
              <p data-id="element-138">Here's what's actually happening:</p>
              <p data-id="element-139">
                Hair follicles don't die suddenly. They enter a state of
                progressive dormancy — a weakened, energy-starved condition
                driven by reduced scalp blood flow, chronic low-grade
                inflammation, and declining cellular energy production. DHT
                sensitivity accelerates the process, but it isn't the full
                story.
              </p>
              <p data-id="element-140">
                The distinction matters because{' '}
                <strong className="text-[#1A1A1A]" data-id="element-141">
                  dormant follicles can be reactivated. Dead ones cannot. The
                  earlier you intervene, the more follicles are still in play.
                </strong>
              </p>

              <h3 className="text-2xl md:text-3xl font-bold text-[#1A1A1A] tracking-tight pt-6" data-id="element-142">
                The Second Lever: Cellular Energy
              </h3>
              <p data-id="element-143">
                When most men think about hair loss, they think DHT. But
                dermatology research has identified a second biological pathway
                — one that operates entirely independently of your hormones.
              </p>
              <p data-id="element-144">
                <strong className="text-[#1A1A1A]" data-id="element-145">
                  This is a metabolic event, not a hormonal one.
                </strong>{' '}
                Which means there is a non-hormonal intervention point — if you
                use the right mechanism to reach it.
              </p>

              <h3 className="text-2xl md:text-3xl font-bold text-[#1A1A1A] tracking-tight pt-6" data-id="element-146">
                Where the Science Comes From
              </h3>
              <p data-id="element-147">
                Photobiomodulation — low-level light therapy — was originally
                studied for wound healing and tissue regeneration. Researchers
                noticed an unexpected finding in treated areas: measurable
                improvements in hair density.
              </p>
              <p data-id="element-148">
                Subsequent dermatology trials confirmed it. Multiple
                sham-controlled studies, peer-reviewed and reviewed by the FDA
                as part of its 510(k) clearance process for at-home devices,
                have documented improvements in hair density in some men with
                androgenetic alopecia.
              </p>
              <p data-id="element-149">
                Not anecdotes. Controlled trials. The mechanism is documented.
                The clearance pathway is real.
              </p>
              <p className="text-xl font-bold text-[#1A1A1A]" data-id="element-150">
                Not a hormone blocker. A follicle activator.
              </p>
            </div>
          </motion.div>

          {/* AT A GLANCE Card */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="lg:col-span-1" data-id="element-151">
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden sticky top-8" data-id="element-152">
              <div className="h-1 bg-[#C8A04A]" data-id="element-153" />
              <div className="p-8" data-id="element-154">
                <h4 className="text-xs font-bold tracking-[0.2em] uppercase text-[#C8A04A] mb-6" data-id="element-155">
                  At a Glance
                </h4>

                <div className="space-y-5" data-id="element-156">
                  <div data-id="element-157">
                    <p className="text-xs font-medium tracking-wider uppercase text-[#9CA3AF] mb-1" data-id="element-158">
                      What it is
                    </p>
                    <p className="text-[#1A1A1A] font-medium" data-id="element-159">
                      Non-hormonal red light protocol for early–moderate
                      thinning
                    </p>
                  </div>
                  <div className="h-px bg-gray-100" data-id="element-160" />
                  <div data-id="element-161">
                    <p className="text-xs font-medium tracking-wider uppercase text-[#9CA3AF] mb-1" data-id="element-162">
                      Daily time
                    </p>
                    <p className="text-[#1A1A1A] font-medium" data-id="element-163">
                      10 minutes — hands-free
                    </p>
                  </div>
                  <div className="h-px bg-gray-100" data-id="element-164" />
                  <div data-id="element-165">
                    <p className="text-xs font-medium tracking-wider uppercase text-[#9CA3AF] mb-1" data-id="element-166">
                      Price
                    </p>
                    <p className="text-[#1A1A1A] font-medium" data-id="element-167">
                      $497 — one-time investment
                    </p>
                  </div>
                  <div className="h-px bg-gray-100" data-id="element-168" />
                  <div data-id="element-169">
                    <p className="text-xs font-medium tracking-wider uppercase text-[#9CA3AF] mb-1" data-id="element-170">
                      Guarantee
                    </p>
                    <p className="text-[#1A1A1A] font-medium" data-id="element-171">
                      180-day results guarantee
                    </p>
                  </div>
                  <div className="h-px bg-gray-100" data-id="element-172" />
                  <div data-id="element-173">
                    <p className="text-xs font-medium tracking-wider uppercase text-[#9CA3AF] mb-1" data-id="element-174">
                      Who it's for
                    </p>
                    <p className="text-[#1A1A1A] font-medium" data-id="element-175">
                      Men who still have hair to protect
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="mt-16 text-center" data-id="element-176">
          <p className="text-lg font-medium text-[#1A1A1A] mb-6" data-id="element-177">
            Act while your follicles are still in play.
          </p>
          <CTAButton data-id="element-178" />
        </motion.div>
      </div>
    </section>;
}